import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api, fmt } from "@/lib/api";

const STATUS_STYLES = {
  PENDING: "bg-chalk/10 text-chalk/60",
  PAID: "bg-gold/25 text-crispy",
  PREPARING: "bg-artichoke/15 text-artichoke",
  READY: "bg-grape/15 text-grape",
  COMPLETED: "bg-chalk/5 text-chalk/40",
  PAYMENT_FAILED: "bg-red-100 text-red-700",
  CHECKOUT_ERROR: "bg-red-100 text-red-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const NEXT_STEP = {
  PAID: { to: "PREPARING", label: "Start preparing" },
  PREPARING: { to: "READY", label: "Mark ready" },
  READY: { to: "COMPLETED", label: "Complete" },
};

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch {
      /* keep old list */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const advance = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status }).catch(() => {});
    load();
  };

  if (loading) return <p className="py-16 text-center text-chalk/50">Loading orders…</p>;

  if (orders.length === 0) {
    return (
      <p data-testid="orders-empty" className="py-16 text-center text-sm text-chalk/50">
        No orders yet. Paid online orders will show up here automatically.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-chalk/50">{orders.length} order{orders.length > 1 ? "s" : ""} · refreshes every 15s</p>
        <button data-testid="orders-refresh-button" onClick={load} className="flex items-center gap-1.5 rounded-full border-2 border-chalk/10 px-4 py-1.5 text-xs font-bold text-chalk/60 hover:border-artichoke hover:text-artichoke">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {orders.map((o) => (
        <div key={o.id} data-testid={`order-card-${o.id}`} className="rounded-3xl border border-leafline bg-cream p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-display text-lg font-semibold text-deep">{o.customer?.name || "Customer"}</p>
              <p className="text-xs text-chalk/50">
                {o.customer?.phone} · Pickup: {o.pickup_time} ·{" "}
                {new Date(o.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid={`order-status-${o.id}`} className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${STATUS_STYLES[o.status] || STATUS_STYLES.PENDING}`}>
                {o.status.replace(/_/g, " ")}
              </span>
              <span className="font-display text-xl font-bold text-deep">{fmt(o.total_cents)}</span>
            </div>
          </div>
          <div className="mt-3 space-y-1 border-t border-dashed border-chalk/10 pt-3">
            {o.items.map((it) => (
              <div key={it.item_id} className="flex justify-between text-sm">
                <span>
                  {it.qty} × {it.title}
                  {it.build && <span className="block text-xs text-chalk/50">{it.build}</span>}
                </span>
                <span className="font-semibold">{fmt(it.unit_price_cents * it.qty)}</span>
              </div>
            ))}
          </div>
          {o.clover_fired ? (
            <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-artichoke" data-testid={`order-kds-ok-${o.id}`}>
              On Clover KDS ✓
            </p>
          ) : o.clover_fire_error ? (
            <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-red-600" data-testid={`order-kds-err-${o.id}`}>
              Clover KDS send failed — check Clover
            </p>
          ) : null}
          {(NEXT_STEP[o.status] || ["PAID", "PREPARING", "READY"].includes(o.status)) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {NEXT_STEP[o.status] && (
                <button
                  data-testid={`order-advance-${o.id}`}
                  onClick={() => advance(o.id, NEXT_STEP[o.status].to)}
                  className="rounded-full bg-artichoke px-5 py-2 text-sm font-bold text-cream transition-colors hover:bg-deep"
                >
                  {NEXT_STEP[o.status].label}
                </button>
              )}
              {["PAID", "PREPARING", "READY"].includes(o.status) && (
                <button
                  data-testid={`order-cancel-${o.id}`}
                  onClick={() => advance(o.id, "CANCELLED")}
                  className="rounded-full border-2 border-red-200 px-5 py-2 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
